import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaSun,
  FaMoon,
  FaList,
  FaUser,
  FaHistory,
  FaSyncAlt,
  FaGift, FaMoneyBillWave
} from 'react-icons/fa';
import {
  IoSparkles,
  IoSearch,
  IoRocket,
  IoTimeOutline,
  IoCloseCircle,
  IoCheckmarkDone,
  IoInformationCircle,
} from 'react-icons/io5';
import { getPlanOffline } from './offline/offlinePlanStore';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

const BUILD_META = {
  ai_queue: {
    title: 'در حال ساخت',
    color: '#6366f1',
    emoji: '🤖',
    Icon: IoSparkles,
  },
  coach_review: {
    title: 'بازبینی مربی',
    color: '#0ea5e9',
    emoji: '👀',
    Icon: IoSearch,
  },
  ready: {
    title: 'فعال',
    color: '#22c55e',
    emoji: '🚀',
    Icon: IoRocket,
  },
};

const REVISION_META = {
  pending_revision: {
    title: 'درخواست اصلاح ثبت شد',
    color: '#a855f7',
    sub: 'برنامه فعلی قابل مشاهده است',
    Icon: IoTimeOutline,
  },
  revision_in_review: {
    title: 'در حال بررسی اصلاح',
    color: '#6366f1',
    sub: 'برنامه فعلی قابل مشاهده است',
    Icon: IoSearch,
  },
  revision_rejected: {
    title: 'اصلاح رد شد',
    color: '#ef4444',
    sub: 'برنامه فعلی پابرجاست',
    Icon: IoCloseCircle,
  },
  revised: {
    title: 'اصلاح انجام شد',
    color: '#10b981',
    sub: 'نسخه جدید آماده است',
    Icon: IoCheckmarkDone,
  },
};

const BUILD_ORDER = ['ai_queue', 'coach_review', 'ready'];

const VIEWABLE_STAGE_FALLBACK = new Set([
  'coach_review',
  'ready',
  'pending_revision',
  'revision_in_review',
  'revision_rejected',
  'revised',
]);

const REVISION_VIEWABLE_STAGES = new Set([
  'pending_revision',
  'revision_in_review',
  'revision_rejected',
  'revised',
]);

function normalizeStage(stage) {
  const s = String(stage || '').toLowerCase().trim();
  if (!s) return 'none';

  if (s === 'ai_queue') return 'ai_queue';
  if (s === 'coach_review') return 'coach_review';
  if (s === 'ready') return 'ready';
  if (s === 'pending_revision') return 'pending_revision';
  if (s === 'revision_in_review') return 'revision_in_review';
  if (s === 'revision_rejected') return 'revision_rejected';
  if (s === 'revised') return 'revised';
  if (s === 'none') return 'none';

  if (s === 'pendingrevision') return 'pending_revision';
  if (s === 'revisioninreview') return 'revision_in_review';
  if (s === 'revisionrejected') return 'revision_rejected';

  return 'none';
}

function isRevisionStage(stageKey) {
  return !!REVISION_META[stageKey];
}

function resolveBuildStage(stageKey) {
  if (stageKey === 'none') return 'none';
  if (stageKey === 'ai_queue') return 'ai_queue';
  if (stageKey === 'coach_review') return 'coach_review';
  if (stageKey === 'ready') return 'ready';

  if (isRevisionStage(stageKey)) return 'ready';

  return 'none';
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [dark, setDark] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');
  const [activePlan, setActivePlan] = useState(null);
  const [offlineMode, setOfflineMode] = useState(false);
  const [offlineSavedAt, setOfflineSavedAt] = useState(null);

  const styles = useMemo(() => {
    const bg = dark ? '#0e1015' : '#ffffff';
    const card = dark ? '#1a1d2e' : '#ffffff';
    const text = dark ? '#ffffff' : '#0f172a';
    const sub = dark ? '#cbd5e1' : '#475569';
    const border = dark ? '#273043' : '#e5e7eb';
    const primary = '#2563eb';
    const accent = '#10b981';
    const muted = dark ? '#334155' : '#cbd5e1';
    const chipBg = dark ? '#141827' : '#f8fafc';

    return { bg, card, text, sub, border, primary, accent, muted, chipBg };
  }, [dark]);

  const goToLogin = useCallback(() => {
    localStorage.removeItem('token');
    navigate('/login');
  }, [navigate]);

  const buildOfflinePlanFallback = useCallback(async () => {
    const cachedPlanId = localStorage.getItem('planId');

    if (!cachedPlanId) {
      setActivePlan(null);
      setOfflineMode(false);
      setOfflineSavedAt(null);
      setErr('اتصال به اینترنت برقرار نیست و برنامه‌ای هم برای نمایش آفلاین پیدا نشد.');
      return;
    }

    const cached = await getPlanOffline(cachedPlanId);

    if (!cached?.data) {
      setActivePlan(null);
      setOfflineMode(false);
      setOfflineSavedAt(null);
      setErr('اتصال به اینترنت برقرار نیست. برای استفاده آفلاین، یک بار برنامه را در حالت آنلاین باز کنید.');
      return;
    }

    setActivePlan({
      hasAnyPlan: true,
      stage: 'ready',
      trackingCode: cachedPlanId,
      title: 'برنامه ذخیره‌شده',
      planType: 'آفلاین',
      requestId: cachedPlanId,
      canOpenPlan: true,
      offline: true,
    });

    setOfflineMode(true);
    setOfflineSavedAt(cached.savedAt || null);
    setErr('اتصال به اینترنت برقرار نیست. برنامه ذخیره‌شده آفلاین در دسترس است.');
  }, []);

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    setOfflineMode(false);
    setOfflineSavedAt(null);

    try {
      const token = localStorage.getItem('token');

      if (!token || !token.trim()) {
        navigate('/login');
        return;
      }

      const res = await fetch(`${API_URL}/api/plan/active`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('planId');
        navigate('/login');
        return;
      }

      if (!res.ok) {
        throw new Error('دریافت داشبورد ناموفق بود.');
      }

      const raw = await res.json();
      const data = raw?.result ?? raw?.Result ?? raw ?? null;

      if (!data?.hasAnyPlan) {
        localStorage.removeItem('planId');
        setActivePlan(null);
        return;
      }

      const stageKey = normalizeStage(data.stage);

      const next = {
        ...data,
        stage: stageKey,
      };

      setActivePlan(next);

      if (data.requestId) {
        localStorage.setItem('planId', String(data.requestId));
      }
    } catch (e) {
      console.error('Dashboard load error:', e);
      await buildOfflinePlanFallback();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate, buildOfflinePlanFallback]);

  useEffect(() => {
    load();
  }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
  };

  const goToActivePlan = () => {
    const planId = activePlan?.requestId || localStorage.getItem('planId');

    if (!planId) {
      alert('ابتدا یک برنامه بسازید.');
      navigate('/plans');
      return;
    }

    localStorage.setItem('planId', String(planId));
    navigate('/active-plan');
  };

  const stageKey = activePlan?.stage || 'none';

  const canOpen =
    typeof activePlan?.canOpenPlan === 'boolean'
      ? activePlan.canOpenPlan
      : VIEWABLE_STAGE_FALLBACK.has(stageKey) && !!activePlan?.requestId;

  const canRequestRevision =
    !offlineMode && canOpen && !!activePlan?.requestId;

  const canSeeRevisionDetails =
    !offlineMode &&
    REVISION_VIEWABLE_STAGES.has(stageKey) &&
    !!activePlan?.requestId;

  const buildStage = resolveBuildStage(stageKey);

  const buildProgress = (() => {
    const idx = BUILD_ORDER.indexOf(buildStage);
    if (idx < 0) return 0;
    return (idx + 1) / BUILD_ORDER.length;
  })();

  const buildMeta = BUILD_META[buildStage] || null;
  const revisionMeta = offlineMode ? null : REVISION_META[stageKey] || null;

  return (
    <div className={dark ? 'dark' : ''}>
      <div
        className="min-h-screen font-vazir"
        style={{
          backgroundColor: styles.bg,
          color: styles.text,
          direction: 'rtl',
        }}
      >
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-xl md:text-2xl font-bold">سلام 👋</h1>

            <div className="flex items-center gap-2">
              <button
                onClick={onRefresh}
                disabled={loading || refreshing}
                className="px-3 py-2 rounded-full border transition disabled:opacity-60"
                style={{
                  backgroundColor: styles.chipBg,
                  borderColor: styles.border,
                }}
                title="بروزرسانی"
              >
                <FaSyncAlt
                  className={refreshing ? 'animate-spin' : ''}
                  color={dark ? '#cbd5e1' : '#334155'}
                  size={14}
                />
              </button>

              <button
                onClick={() => setDark((v) => !v)}
                className="p-3 rounded-full border transition"
                style={{
                  backgroundColor: dark ? '#141827' : '#f1f5f9',
                  borderColor: styles.border,
                }}
                aria-label="toggle theme"
              >
                {dark ? (
                  <FaSun className="text-yellow-300" size={16} />
                ) : (
                  <FaMoon className="text-slate-900" size={16} />
                )}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="min-h-[50vh] flex flex-col items-center justify-center">
              <div
                className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
                style={{
                  borderColor: styles.primary,
                  borderTopColor: 'transparent',
                }}
              />
              <p className="mt-3 text-sm" style={{ color: styles.sub }}>
                در حال بارگذاری داشبورد...
              </p>
            </div>
          ) : (
            <>
              {!!err && (
                <div
                  className={`mb-4 rounded-2xl border px-4 py-3 text-sm text-right leading-7 ${
                    offlineMode
                      ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-200'
                      : 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-200'
                  }`}
                >
                  {err}

                  {offlineMode && offlineSavedAt && (
                    <div className="text-xs mt-1 opacity-80">
                      آخرین ذخیره برنامه:{' '}
                      {new Date(offlineSavedAt).toLocaleString('fa-IR')}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                  <div
                    className="rounded-2xl border p-4"
                    style={{
                      backgroundColor: styles.card,
                      borderColor: styles.border,
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-base md:text-lg font-bold">
                        وضعیت برنامه
                      </h2>

                      {buildMeta ? (
                        <div
                          className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
                          style={{
                            borderColor: styles.border,
                            backgroundColor: styles.chipBg,
                          }}
                        >
                          <buildMeta.Icon size={16} color={buildMeta.color} />
                          <span
                            className="text-xs font-semibold"
                            style={{ color: buildMeta.color }}
                          >
                            {offlineMode ? 'آفلاین' : buildMeta.title}
                          </span>
                        </div>
                      ) : (
                        <div
                          className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
                          style={{
                            borderColor: styles.border,
                            backgroundColor: styles.chipBg,
                          }}
                        >
                          <IoInformationCircle size={16} color={styles.sub} />
                          <span
                            className="text-xs font-semibold"
                            style={{ color: styles.sub }}
                          >
                            بدون برنامه
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-4xl animate-bounce">
                        {activePlan
                          ? offlineMode
                            ? '📦'
                            : buildStage === 'ready'
                            ? '🚀'
                            : buildStage === 'coach_review'
                            ? '👀'
                            : buildStage === 'ai_queue'
                            ? '🤖'
                            : 'ℹ️'
                          : 'ℹ️'}
                      </div>

                      <div className="flex-1 text-right">
                        {activePlan ? (
                          <>
                            <p
                              className="text-sm"
                              style={{ color: styles.sub }}
                            >
                              {activePlan.title
                                ? `${activePlan.title} — ${activePlan.planType || ''}`
                                : ''}
                            </p>

                            <p className="mt-1 font-semibold">
                              {offlineMode
                                ? 'برنامه ذخیره‌شده آماده مشاهده است'
                                : buildStage === 'ai_queue'
                                ? 'در حال آماده‌سازی برنامه...'
                                : buildStage === 'coach_review'
                                ? 'در حال بررسی مربی...'
                                : buildStage === 'ready'
                                ? 'برنامه آماده است'
                                : 'هنوز برنامه فعالی نداری.'}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm" style={{ color: styles.sub }}>
                            هنوز برنامه فعالی نداری.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-4">
                      <div
                        className="h-2.5 rounded-full overflow-hidden"
                        style={{
                          backgroundColor: dark ? '#0f172a' : '#e5e7eb',
                        }}
                      >
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.max(6, buildProgress * 100)}%`,
                            backgroundColor:
                              buildMeta?.color ||
                              (dark ? '#334155' : '#94a3b8'),
                          }}
                        />
                      </div>

                      <div className="flex justify-between mt-2 gap-2">
                        {BUILD_ORDER.map((k) => (
                          <span
                            key={k}
                            className="text-[11px]"
                            style={{
                              color:
                                k === buildStage
                                  ? BUILD_META[k]?.color || styles.sub
                                  : styles.sub,
                              fontWeight: k === buildStage ? 700 : 400,
                            }}
                          >
                            {BUILD_META[k].title}
                          </span>
                        ))}
                      </div>
                    </div>

                    {activePlan ? (
                      <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <button
                          onClick={canOpen ? goToActivePlan : undefined}
                          disabled={!canOpen}
                          className="flex-1 py-3 rounded-xl text-white font-medium transition"
                          style={{
                            backgroundColor: canOpen
                              ? styles.primary
                              : styles.muted,
                            opacity: canOpen ? 1 : 0.65,
                            cursor: canOpen ? 'pointer' : 'not-allowed',
                          }}
                        >
                          {offlineMode ? 'مشاهده برنامه آفلاین' : 'رفتن به برنامه'}
                        </button>

                        {canRequestRevision && (
                          <button
                            onClick={() =>
                              navigate(`/revision-request/${activePlan.requestId}`)
                            }
                            className="flex-1 py-3 rounded-xl font-medium transition"
                            style={{
                              backgroundColor: '#f59e0b',
                              color: '#fff',
                            }}
                          >
                            درخواست اصلاح
                          </button>
                        )}

                        {!canOpen && (
                          <button
                            onClick={() =>
                              alert(
                                'برنامه هنوز ساخته نشده یا در حال آماده‌سازی است. لطفاً کمی بعد دوباره بررسی کنید.'
                              )
                            }
                            className="px-4 py-3 rounded-xl border"
                            style={{
                              borderColor: styles.border,
                              backgroundColor: styles.chipBg,
                            }}
                            title="وضعیت برنامه"
                          >
                            <IoTimeOutline
                              size={18}
                              color={dark ? '#cbd5e1' : '#334155'}
                            />
                          </button>
                        )}
                      </div>
                    ) : (
                      <Link
                        to="/plans"
                        className="block mt-4 text-center py-3 rounded-xl font-medium"
                        style={{
                          backgroundColor: styles.accent,
                          color: '#0f172a',
                        }}
                      >
                        ساخت برنامه جدید
                      </Link>
                    )}
                  </div>

                  <ReferralDashboardCard
                    styles={styles}
                    navigate={navigate}
                  />

                  {revisionMeta ? (
                    <div
                      className="rounded-2xl border p-4"
                      style={{
                        backgroundColor: styles.card,
                        borderColor: styles.border,
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-base md:text-lg font-bold">
                          وضعیت اصلاح
                        </h2>

                        <div
                          className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
                          style={{
                            borderColor: styles.border,
                            backgroundColor: styles.chipBg,
                          }}
                        >
                          <revisionMeta.Icon
                            size={16}
                            color={revisionMeta.color}
                          />
                          <span
                            className="text-xs font-semibold"
                            style={{ color: revisionMeta.color }}
                          >
                            {revisionMeta.title}
                          </span>
                        </div>
                      </div>

                      <p
                        className="text-sm leading-7 text-right"
                        style={{ color: styles.sub }}
                      >
                        {revisionMeta.sub}
                      </p>

                      {canSeeRevisionDetails && (
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={() =>
                              navigate(`/revision-details/${activePlan.requestId}`)
                            }
                            className="px-3 py-2 rounded-xl border flex items-center gap-2"
                            style={{
                              borderColor: styles.border,
                              backgroundColor: styles.chipBg,
                              color: dark ? '#e2e8f0' : '#0f172a',
                            }}
                          >
                            <IoInformationCircle size={16} />
                            <span className="text-xs font-medium">
                              مشاهده درخواست اصلاح
                            </span>
                          </button>
                        </div>
                      )}

                      {stageKey === 'revised' ? (
                        <p
                          className="mt-3 text-sm leading-7 text-right"
                          style={{ color: styles.sub }}
                        >
                          نسخه جدید جایگزین برنامه قبلی شده است.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="space-y-4">
                  <QuickCard
                    title="برنامه‌ها"
                    sub="مشاهده"
                    icon={<FaList size={18} />}
                    onClick={() => navigate('/plans')}
                    styles={styles}
                  />

                  <QuickCard
                    title="پروفایل"
                    sub="مشاهده"
                    icon={<FaUser size={18} />}
                    onClick={() => navigate('/profile')}
                    styles={styles}
                  />

                  <QuickCard
                    title="تاریخچه"
                    sub="مشاهده"
                    icon={<FaHistory size={18} />}
                    onClick={() => navigate('/history')}
                    styles={styles}
                  />

                  <button
                    onClick={goToLogin}
                    className="w-full py-3 rounded-2xl border text-sm"
                    style={{
                      borderColor: styles.border,
                      backgroundColor: styles.chipBg,
                      color: styles.sub,
                    }}
                  >
                    خروج و ورود مجدد
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function QuickCard({ title, sub, icon, onClick, styles }) {
  return (
    <button
      onClick={onClick}
      className="w-full rounded-2xl border p-4 text-right transition hover:scale-[1.01]"
      style={{
        backgroundColor: styles.card,
        borderColor: styles.border,
      }}
    >
      <div className="flex items-center justify-between">
        <span className="font-bold" style={{ color: styles.text }}>
          {title}
        </span>
        <span style={{ color: styles.sub }}>{icon}</span>
      </div>

      <p className="text-xs mt-2" style={{ color: styles.sub }}>
        {sub}
      </p>
    </button>
  );
}

function ReferralDashboardCard({ styles, navigate }) {
  return (
    <div
      className="rounded-2xl border p-4 overflow-hidden relative"
      style={{
        background:
          'linear-gradient(135deg, rgba(124,58,237,0.22), rgba(37,99,235,0.14), rgba(16,185,129,0.10))',
        borderColor: styles.border,
      }}
    >
      <div className="absolute -top-10 -left-10 w-28 h-28 rounded-full bg-purple-500/20 blur-2xl" />
      <div className="absolute -bottom-12 -right-10 w-32 h-32 rounded-full bg-green-500/15 blur-2xl" />

      <div className="relative z-10">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-l from-purple-600 to-blue-500 text-white flex items-center justify-center">
              <FaGift size={18} />
            </div>

            <div>
              <h2 className="text-base md:text-lg font-bold">
                دعوت دوستان
              </h2>
              <p className="text-xs mt-1" style={{ color: styles.sub }}>
                دوستت تخفیف می‌گیرد، تو درآمد کسب می‌کنی
              </p>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border"
            style={{
              borderColor: styles.border,
              backgroundColor: styles.chipBg,
              color: styles.sub,
            }}
          >
            <FaMoneyBillWave />
            کسب درآمد
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div
            className="rounded-2xl border p-3"
            style={{
              borderColor: styles.border,
              backgroundColor: 'rgba(255,255,255,0.04)',
            }}
          >
            <div className="text-xs" style={{ color: styles.sub }}>
              موجودی
            </div>
            <div className="mt-1 font-black" style={{ color: styles.text }}>
              قابل مشاهده در صفحه دعوت
            </div>
          </div>

          <div
            className="rounded-2xl border p-3"
            style={{
              borderColor: styles.border,
              backgroundColor: 'rgba(255,255,255,0.04)',
            }}
          >
            <div className="text-xs" style={{ color: styles.sub }}>
              تعداد دعوت
            </div>
            <div className="mt-1 font-black" style={{ color: styles.text }}>
              گزارش کامل
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate('/referral')}
          className="mt-4 w-full py-3 rounded-xl font-bold text-white bg-gradient-to-l from-purple-600 to-blue-500 shadow-lg shadow-purple-500/20"
        >
          کسب درآمد
        </button>
      </div>
    </div>
  );
}